import { supabase } from "../supabase";

/**
 * Firebase Auth 대체.
 *
 * 주의 — 기존 유저의 uid 가 바뀐다.
 * 마이그레이션 스크립트가 Firebase 이메일로 Supabase 계정을 미리 만들어 두었으므로,
 * 같은 Google 계정으로 로그인하면 이메일이 일치해 기존 데이터에 연결된다.
 * (Supabase 대시보드 > Authentication > Providers > Google 에서
 *  "Skip nonce check" 가 아니라 이메일 자동 연결 동작을 확인할 것)
 */

export async function signInWithGoogle() {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: window.location.origin },
  });
  if (error) throw error;
}

/** 게스트 모드. Supabase 대시보드에서 Anonymous sign-in 을 켜야 동작한다. */
export async function signInAsGuest() {
  const { data, error } = await supabase.auth.signInAnonymously();
  if (error) throw error;
  return data.user;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

/**
 * onAuthStateChanged(auth, cb) 대체.
 * 화면 코드가 기대하는 모양({ uid, email, displayName, isAnonymous })으로 맞춰준다.
 */
export function onAuthChange(callback) {
  const toAppUser = (user) =>
    user
      ? {
          uid: user.id,
          email: user.email ?? null,
          displayName:
            user.user_metadata?.full_name ??
            user.user_metadata?.name ??
            user.email?.split("@")[0] ??
            null,
          // 익명 로그인은 is_anonymous 플래그로 구분된다
          isAnonymous: user.is_anonymous === true,
        }
      : null;

  // 한 번의 로그인에도 콜백이 여러 번 온다:
  // getSession() 과 onAuthStateChange 가 각각 발화하고, OAuth 로 돌아올 때는
  // INITIAL_SESSION -> SIGNED_IN 이 잇따라 온다.
  // 그때마다 새 객체를 넘기면 구독자의 effect 가 매번 재시작되어
  // 진행 중이던 로딩이 취소되고 화면이 넘어가지 못한다.
  // 실제로 사람이 바뀐 경우에만 알린다.
  var last = "\u0000"; // 아직 아무것도 알리지 않은 상태
  var emit = (user) => {
    var key = user ? user.uid + "|" + user.isAnonymous : "";
    if (key === last) return;
    last = key;
    callback(user);
  };

  supabase.auth.getSession().then(({ data }) => emit(toAppUser(data.session?.user ?? null)));

  const { data: sub } = supabase.auth.onAuthStateChange((_event, session) =>
    emit(toAppUser(session?.user ?? null))
  );
  return () => sub.subscription.unsubscribe();
}

/**
 * 회원 탈퇴.
 * Firestore 시절엔 DeleteAccountModal.js 가 다른 유저 문서까지 돌며 80줄로 정리했다.
 * 이제 profiles 행 하나만 지우면 on delete cascade 가 전부 정리한다.
 * auth.users 삭제는 service_role 권한이 필요해 Edge Function 으로 뺀다.
 */
export async function deleteAccount() {
  const { error } = await supabase.functions.invoke("delete-account");
  if (error) throw error;
  await supabase.auth.signOut();
}
